import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

type ListInput = Parameters<typeof api.notification.list>[0];
type TemplatesInput = Parameters<typeof api.notification.templates>[0];
type PreferenceInput = Parameters<typeof api.notification.setPreference>[0];

/** How often the bell asks again. No websocket: §7.1 buys a poll, not a socket. */
const CENTRE_POLL_MS = 60_000;

/**
 * The notification screens' reads. The operator's list, one delivery, the
 * template preview, and the three reads that belong to the signed-in person
 * themselves — their own centre and their own preferences, which carry no
 * permission because the caller is the whole authorization.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: ['notifications', 'list'] as const,
  list: (input: ListInput) => ['notifications', 'list', input] as const,
  counts: (input: ListInput) => ['notifications', 'counts', input] as const,
  detail: (id: string) => ['notifications', 'detail', id] as const,
  templates: (input: TemplatesInput) => ['notifications', 'templates', input] as const,
  mine: ['notifications', 'mine'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

export function useNotifications(input: ListInput) {
  return useQuery({
    queryKey: notificationKeys.list(input),
    queryFn: () => api.notification.list(input),
    placeholderData: keepPreviousData,
  });
}

export function useNotification(id: string | null) {
  return useQuery({
    queryKey: notificationKeys.detail(id ?? ''),
    queryFn: () => api.notification.get({ id: id ?? '' }),
    enabled: id !== null,
  });
}

export function useNotificationTemplates(channel?: TemplatesInput['channel']) {
  const input: TemplatesInput = channel ? { channel } : {};
  return useQuery({
    queryKey: notificationKeys.templates(input),
    queryFn: () => api.notification.templates(input),
  });
}

/** The bell. Polled rather than pushed, and only while a page holding it is open. */
export function useMyNotifications() {
  return useQuery({
    queryKey: notificationKeys.mine,
    queryFn: () => api.notification.mine({}),
    refetchInterval: CENTRE_POLL_MS,
    placeholderData: keepPreviousData,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => api.notification.markRead(input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.mine });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.notification.markAllRead({}),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.mine });
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: () => api.notification.preferences({}),
  });
}

/**
 * One channel of one event. The route answers with the whole preference set, so
 * the answer replaces the cache rather than starting a second round trip.
 */
export function useSetNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PreferenceInput) => api.notification.setPreference(input),
    onSuccess: (result) => queryClient.setQueryData(notificationKeys.preferences, result),
  });
}
